// src/pages/FAQs.tsx

import { useState } from 'react'
import { useTheme } from '../contexts/ThemeContext'
import { Search, ChevronDown, ChevronUp, HelpCircle, MessageCircle } from 'lucide-react'

interface FAQ {
  id: string
  category: string
  question: string
  answer: string
}

export default function FAQs() {
  const { isDark } = useTheme()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const categories = [
    { id: 'all', label: 'Todas' },
    { id: 'general', label: 'General' },
    { id: 'tickets', label: 'Tickets' },
    { id: 'cuenta', label: 'Mi Cuenta' },
    { id: 'tecnico', label: 'Soporte Técnico' }
  ]

  const faqs: FAQ[] = [
    {
      id: '1',
      category: 'general',
      question: '¿Cómo puedo contactar con soporte?',
      answer: 'Puedes crear un ticket desde el menú "Tickets" o usar el chat en tiempo real. Nuestro equipo responde en menos de 24 horas.'
    },
    {
      id: '2',
      category: 'tickets',
      question: '¿Cómo creo un nuevo ticket?',
      answer: 'Ve al menú "Tickets" y haz clic en "Nuevo Ticket". Completa el formulario con el título, descripción y categoría. Puedes adjuntar archivos si es necesario.'
    },
    {
      id: '3',
      category: 'tickets',
      question: '¿Cuánto tiempo tarda en resolverse un ticket?',
      answer: 'El tiempo de resolución depende de la prioridad: Crítico (2-4 horas), Alto (24 horas), Medio (48 horas), Bajo (72 horas).'
    },
    {
      id: '4',
      category: 'cuenta',
      question: '¿Cómo cambio mi contraseña?',
      answer: 'Ve a "Mi Cuenta" en el menú de usuario, luego a la sección "Seguridad" y selecciona "Cambiar Contraseña".'
    },
    {
      id: '5',
      category: 'tecnico',
      question: '¿Qué navegadores son compatibles?',
      answer: 'El sistema es compatible con Chrome, Firefox, Safari y Edge en sus últimas versiones. Recomendamos Chrome para mejor rendimiento.'
    },
    {
      id: '6',
      category: 'general',
      question: '¿Puedo adjuntar archivos a los tickets?',
      answer: 'Sí, puedes adjuntar hasta 5 archivos por ticket o comentario. El tamaño máximo por archivo es 10MB. Formatos soportados: PDF, imágenes, documentos.'
    },
    {
      id: '7',
      category: 'tickets',
      question: '¿Cómo veo el estado de mis tickets?',
      answer: 'En el menú "Tickets" verás todos tus tickets con su estado actual: Abierto, En Progreso, Pendiente, Resuelto o Cerrado.'
    },
    {
      id: '8',
      category: 'tecnico',
      question: '¿Qué hago si no puedo acceder al sistema?',
      answer: 'Verifica tu conexión a internet y que estés usando las credenciales correctas. Si el problema persiste, contacta a support@ingeai7.com'
    }
  ]

  const filteredFaqs = faqs.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id)
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} p-6`}>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500/10 rounded-full mb-4">
            <HelpCircle className="w-8 h-8 text-blue-500" />
          </div>
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
            Preguntas Frecuentes
          </h1>
          <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Encuentra respuestas a las preguntas más comunes
          </p>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar preguntas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full pl-12 pr-4 py-4 ${
              isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
            } border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg`}
          />
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2 justify-center">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedCategory === cat.id
                  ? 'bg-blue-600 text-white'
                  : isDark
                  ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* FAQs List */}
        <div className="space-y-3">
          {filteredFaqs.length > 0 ? (
            filteredFaqs.map(faq => (
              <div
                key={faq.id}
                className={`${
                  isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                } border rounded-lg overflow-hidden transition-all`}
              >
                <button
                  onClick={() => toggleExpand(faq.id)}
                  className={`w-full px-6 py-4 flex items-center justify-between text-left hover:bg-opacity-50 transition-colors ${
                    expandedId === faq.id ? (isDark ? 'bg-gray-700/50' : 'bg-gray-50') : ''
                  }`}
                >
                  <div className="flex items-start gap-3 flex-1">
                    <MessageCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                      expandedId === faq.id ? 'text-blue-500' : 'text-gray-400'
                    }`} />
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {faq.question}
                    </span>
                  </div>
                  {expandedId === faq.id ? (
                    <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>

                {expandedId === faq.id && (
                  <div className={`px-6 pb-4 pt-2 ${isDark ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
                    <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'} leading-relaxed pl-8`}>
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <HelpCircle className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-gray-700' : 'text-gray-300'}`} />
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                No se encontraron preguntas
              </p>
            </div>
          )}
        </div>

        {/* Contact Support */}
        <div className={`${
          isDark ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'
        } border rounded-lg p-6 text-center`}>
          <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
            ¿No encontraste lo que buscabas?
          </h3>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
            Nuestro equipo está listo para ayudarte
          </p>
          <button
            onClick={() => window.location.href = '/dashboard/tickets/create'}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            Crear Ticket de Soporte
          </button>
        </div>
      </div>
    </div>
  )
}